import type { FC } from "react"
import { useMemo, useState } from "react"
import {
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from "recharts"
import type { Payload } from "recharts/types/component/DefaultTooltipContent"

import { cn } from "@/lib/utils"

type RiskPoint = {
  date: string
  score: number | null
}

type RiskThresholds = {
  low: number
  medium: number
  high: number
  max?: number
}

const DEFAULT_THRESHOLDS: RiskThresholds = {
  low: 0,
  medium: 30,
  high: 60,
  max: 100,
}

type TooltipState = {
  payload: Payload<number, string>[]
  coordinate?: { x: number; y: number }
  transform: string
  arrowDirection?: 'bottom' | 'right' | 'left' | 'top'
}

// Custom X-axis tick renderer to show quarter and year in a column layout
// Example: "Q1 2025" → "Q1" (bold, top) and "2025" (regular, below)
const renderXAxisTick = (props: any) => {
  const { x, y, payload } = props
  const rawLabel = String(payload?.value ?? "")

  // Split into quarter + year if possible
  const parts = rawLabel.split(/\s+/).filter(Boolean)
  const quarter = parts[0] || ""
  const year = parts[1] || (parts.length === 1 ? parts[0] : "")

  return (
    <g transform={`translate(${x},${y})`}>
      {quarter && (
        <text
          x={0}
          y={0}
          dy={-2}
          textAnchor="middle"
          style={{
            fill: "var(--risk-axis-tick)",
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          {quarter}
        </text>
      )}
      {year && (
        <text
          x={0}
          y={0}
          dy={13}
          textAnchor="middle"
          style={{
            fill: "var(--risk-axis-tick)",
            fontSize: 11,
            fontWeight: 400,
            opacity: 0.8,
          }}
        >
          {year}
        </text>
      )}
    </g>
  )
}

type RiskStratificationChartProps = {
  data: RiskPoint[]
  thresholds?: RiskThresholds
  className?: string
}

type ExtendedRiskPoint = RiskPoint & {
  rawScore: number | null
  isInterpolated: boolean
}

// Helper function to determine risk level
const getRiskLevel = (score: number, thresholds: RiskThresholds): 'low' | 'medium' | 'high' => {
  if (score >= thresholds.high) return 'high'      // >= 60 → High risk
  if (score >= thresholds.medium) return 'medium'  // >= 30 → Medium risk
  return 'low'                                      // < 30 → Low risk
}

const RiskStratificationChart: FC<RiskStratificationChartProps> = ({
  data,
  thresholds = DEFAULT_THRESHOLDS,
  className,
}) => {
  const [hoveredTooltip, setHoveredTooltip] = useState<TooltipState | null>(null)
  const normalizedData = useMemo(() => interpolateChartData(data), [data])

  const extendedData = useMemo<ExtendedRiskPoint[]>(() => {
    const results = normalizedData.map((point) => {
      const result: ExtendedRiskPoint = {
        ...point,
        rawScore: point.rawScore,
        isInterpolated: point.isInterpolated,
      }

      return result
    })

    console.log('📈 Extended Data for Chart:', results.map(r => ({
      date: r.date,
      score: r.score,
      rawScore: r.rawScore,
      isInterpolated: r.isInterpolated
    })))

    return results
  }, [normalizedData, thresholds])

  // Ensure enough horizontal space so the chart doesn't get overly compressed.
  // Give each point some minimum width, but never less than 400px total.
  const minChartWidth = useMemo(() => {
    const pointCount = Math.max(extendedData.length, 1)
    const perPoint = 80 // px per point
    return Math.max(400, pointCount * perPoint)
  }, [extendedData])

  const chartMax = useMemo(() => {
    const scoreMax = normalizedData.reduce(
      (max, point) => Math.max(max, typeof point.score === "number" ? point.score : 0),
      0
    )
    const fallbackMax = thresholds.max ?? 100
    return Math.max(fallbackMax, scoreMax, thresholds.high + 20)
  }, [normalizedData, thresholds])

  const showDotTooltip = (props: any) => {
    const { cx, cy, payload, index } = props
    const color = "var(--risk-line-color)"
    const value = typeof payload.score === "number" ? payload.score : 0
    const isLastPoint = (typeof index === "number") && index === normalizedData.length - 1

    if (cx == null || cy == null || !payload || payload.score === null) {
      setHoveredTooltip(null)
      return
    }

    // Determine positioning based on point height and position
    let transform: string
    let arrowDirection: 'bottom' | 'right' | 'left' | 'top'

    // For very high points (>= 70% of chart), show tooltip below
    if (value >= chartMax * 0.7) {
      transform = "translate(-50%, 15px)"
      arrowDirection = 'top'
    }
    // For very low points (<= 20% of chart), show tooltip above
    else if (value <= chartMax * 0.2) {
      transform = "translate(-50%, calc(-100% - 15px))" // Shift up to clear dot
      arrowDirection = 'bottom'
    }
    // For last point, show tooltip to the left
    else if (isLastPoint) {
      transform = "translate(calc(-100% - 15px), -50%)" // Shift left
      arrowDirection = 'right'
    }
    else {
      transform = "translate(15px, -50%)" // Shift right
      arrowDirection = 'left'
    }

    setHoveredTooltip({
      payload: [
        {
          dataKey: "score",
          name: "score",
          value: value,
          color,
          payload,
        } as Payload<number, string>,
      ],
      coordinate: { x: cx, y: cy },
      transform,
      arrowDirection,
    })
  }

  const hideDotTooltip = () => setHoveredTooltip(null)

  const renderChartDot = (props: any) => {
    const { cx, cy, payload } = props
    if (payload?.score === null) return null
    const grayColor = "var(--risk-axis-guide)"

    return (
      <circle
        cx={cx}
        cy={cy}
        r={6}
        fill={grayColor}
        stroke="#fff"
        strokeWidth={0}
        style={{ cursor: "pointer" }}
        onMouseEnter={() => showDotTooltip(props)}
        onMouseLeave={hideDotTooltip}
      />
    )
  }

  return (
    <div className={cn("h-[400px] w-full overflow-x-auto", className)}>
      <div className="h-full relative overflow-hidden"
        style={{ minWidth: `${minChartWidth}px` }}
        onMouseLeave={hideDotTooltip}
        onClick={hideDotTooltip}
      >
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={extendedData}
            margin={{
              top: 24,
              bottom: 24,
              left: 40,
              right: 90,
            }}
             onMouseLeave={hideDotTooltip}
            onMouseMove={(state) => {
              if (!state.isTooltipActive) {
                hideDotTooltip()
              }
            }}
          >
            <defs>
              <linearGradient id="risk-gradient" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="var(--risk-gradient-start)" />
                <stop offset="55%" stopColor="var(--risk-gradient-mid)" />
                <stop offset="100%" stopColor="var(--risk-gradient-end)" />
              </linearGradient>
              <linearGradient id="risk-gradient-medium" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="var(--risk-gradient-start-medium)" />
                <stop offset="55%" stopColor="var(--risk-gradient-mid-medium)" />
                <stop offset="100%" stopColor="var(--risk-gradient-end-medium)" />
              </linearGradient>
              <linearGradient id="risk-gradient-high" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="var(--risk-gradient-start-high)" />
                <stop offset="55%" stopColor="var(--risk-gradient-mid-high)" />
                <stop offset="100%" stopColor="var(--risk-gradient-end-high)" />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="var(--risk-grid-stroke)" vertical={true} />
            <XAxis
              dataKey="date"
              tick={renderXAxisTick}
              tickMargin={16}
              axisLine={{ stroke: "var(--risk-axis-stroke)", strokeWidth: 1 }}
              tickLine={false}
              interval={0}
            />
            <YAxis
              label={{
                value: "%",
                angle: 90,
                position: "insideLeft",
                style: {
                  textAnchor: "middle",
                  fill: "var(--risk-axis-tick)",
                  fontSize: 14,
                  fontWeight: 500
                },
              }}
              tick={{ fill: "var(--risk-axis-tick)", fontSize: 12 }}
              tickMargin={12}
              axisLine={{ stroke: "var(--risk-axis-stroke)", strokeWidth: 1 }}
              tickLine={false}
              domain={[0, chartMax]}
            />
            <Tooltip active={false} />
            {/* Background bands for low, medium, and high risk ranges */}
            <ReferenceArea
              y1={thresholds.low}
              y2={thresholds.medium}
              fill="var(--risk-low-color)"
              fillOpacity={0.2}
              ifOverflow="extendDomain"
            />
            <ReferenceArea
              y1={thresholds.medium}
              y2={thresholds.high}
              fill="var(--risk-medium-color)"
              fillOpacity={0.2}
              ifOverflow="extendDomain"
            />
            <ReferenceArea
              y1={thresholds.high}
              y2={chartMax}
              fill="var(--risk-high-color)"
              fillOpacity={0.2}
              ifOverflow="extendDomain"
            />

            {/* Render single line with connected nulls */}
            <Line
              type="linear"
              dataKey="score"
              stroke="var(--risk-axis-guide)"
              strokeWidth={2}
                  dot={renderChartDot}
                  activeDot={renderChartDot}
              connectNulls={true}
              isAnimationActive={false}
            />
            {normalizedData.length ? (
              <>
                <ReferenceLine
                  y={0}
                  stroke="var(--risk-axis-guide)"
                  strokeWidth={1.5}
                  strokeLinecap="round"
                  ifOverflow="extendDomain"
                />
                <ReferenceLine
                  x={normalizedData[0].date}
                  stroke="var(--risk-axis-guide)"
                  strokeWidth={1.5}
                  strokeLinecap="round"
                  ifOverflow="extendDomain"
                />
              </>
            ) : null}
          </ComposedChart>
        </ResponsiveContainer>
        {hoveredTooltip ? (
          <div
            className="pointer-events-none absolute"
            style={{
              left: hoveredTooltip.coordinate?.x ?? 0,
              top: hoveredTooltip.coordinate?.y ?? 0,
              transform: hoveredTooltip.transform,
            }}
          >
            <ChartTooltip
              active
              payload={hoveredTooltip.payload}
              thresholds={thresholds}
              arrowDirection={hoveredTooltip.arrowDirection}
            />
          </div>
        ) : null}
      </div>
    </div>
  )
}

type ChartTooltipProps = TooltipProps<number, string> & {
  payload?: ReadonlyArray<Payload<number, string>>
  thresholds: RiskThresholds
  arrowDirection?: 'bottom' | 'right' | 'left' | 'top'
}

const ChartTooltip: FC<ChartTooltipProps> = ({ active, payload, thresholds, arrowDirection }) => {
  if (!active || !payload?.length) {
    return null
  }

  // Find the payload entry for the "score" line (the only one with dots)
  const scoreEntry = payload.find((p) => p.dataKey === 'score')
  if (!scoreEntry) {
    return null
  }

  const score = typeof scoreEntry.value === 'number' ? scoreEntry.value : 0

  // Determine risk level to get appropriate color
  const riskLevel = getRiskLevel(score, thresholds)
  const valueColor = (() => {
    if (riskLevel === "high") return "var(--risk-line-color-high)";
    if (riskLevel === "medium") return "var(--risk-line-color-medium)";
    return "var(--risk-line-color)";
  })()

  return (
    <div
      className="pointer-events-none"
    >
      <div
        className="relative rounded-2xl border px-4 py-2 text-center shadow-lg"
        style={{
          backgroundColor: "var(--risk-tooltip-bg)",
          borderColor: "var(--risk-tooltip-border)",
        }}
      >
        <p className="text-xs font-medium whitespace-nowrap" style={{ color: "var(--risk-tooltip-label)" }}>
          Risk Score
        </p>
        <p className="text-lg font-semibold whitespace-nowrap" style={{ color: valueColor }}>
          {score}%
        </p>
        {/* Arrow pointing based on direction */}
        {arrowDirection === 'top' && (
          <span
            className="pointer-events-none absolute top-[-8px] left-1/2 -translate-x-1/2 border-l-[8px] border-r-[8px] border-b-[8px] border-l-transparent border-r-transparent"
            style={{ borderBottomColor: "var(--risk-tooltip-border)" }}
          />
        )}
        {arrowDirection === 'bottom' && (
          <span
            className="pointer-events-none absolute bottom-[-8px] left-1/2 -translate-x-1/2 border-l-[8px] border-r-[8px] border-t-[8px] border-l-transparent border-r-transparent"
            style={{ borderTopColor: "var(--risk-tooltip-border)" }}
          />
        )}
        {arrowDirection === 'right' && (
          <span
            className="pointer-events-none absolute right-[-8px] top-1/2 -translate-y-1/2 border-t-[8px] border-b-[8px] border-l-[8px] border-t-transparent border-b-transparent"
            style={{ borderLeftColor: "var(--risk-tooltip-border)" }}
          />
        )}
        {arrowDirection === 'left' && (
          <span
            className="pointer-events-none absolute left-[-8px] top-1/2 -translate-y-1/2 border-t-[8px] border-b-[8px] border-r-[8px] border-t-transparent border-b-transparent"
            style={{ borderRightColor: "var(--risk-tooltip-border)" }}
          />
        )}
      </div>
    </div>
  )
}

export type { RiskPoint, RiskThresholds }
export { RiskStratificationChart }

type InterpolatedPoint = {
  date: string
  score: number | null
  rawScore: number | null
  isInterpolated: boolean
}

const interpolateChartData = (data: RiskPoint[]): InterpolatedPoint[] => {
  console.log('=== interpolateChartData called ===', { dataLength: data?.length })

  if (!data?.length) return []

  const results: InterpolatedPoint[] = data.map((point) => {
    const rawScore = point.score
    const isNoData = rawScore === null || rawScore === undefined || rawScore === 0

    const scoreValue =
      typeof rawScore === "number" && !isNoData ? rawScore : null

    return {
      date: point.date,
      score: scoreValue,
      rawScore: scoreValue,
      isInterpolated: isNoData,
    }
  })

  // Final log to verify the full dataset
  console.log('Final Data for Chart:', results.map(r => ({
    date: r.date,
    score: r.score,
    rawScore: r.rawScore,
    isInterpolated: r.isInterpolated
  })))

  return results
}